import { NavLink } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useLayoutStore } from '../../stores/layoutStore';
import { VortexIcon, NavIcon, NavIconType } from '../branding';

/**
 * Sidebar navigation component.
 * Sprint 31.7: Rewritten to match mockup-1 exactly using CSS classes.
 * Sprint 31.9 Phase 11: Updated for tenant-aware navigation.
 * Sprint 34.1: Added VortexCA branding with custom nav icons.
 * - Fixed position 260px width
 * - Collapsible with transform:translateX(-270px)
 * - Shadow on right edge
 * - Section headers with group-h pattern
 * - Active item with accent background
 * - Footer section with logo
 * 
 * Sprint 31.7 Update: Simplified to match mockup-1 primary navigation.
 * - Dashboard Explorer (dashboard)
 * - PKI Management (SSL/TLS, ACME, SSH)
 * - Administration link (goes to admin sub-layout)
 * Admin section details are now in AdminLayout sub-sidebar.
 * 
 * Sprint 31.9 Phase 11: Tenant-aware navigation.
 * - Shows tenant context in sidebar header
 * - Navigation items scoped to current tenant
 * - Platform admin items visible only with platform_admin role
 */

interface NavItem {
  path: string;
  label: string;
  icon?: NavIconType;  // Sprint 34.1: Custom nav icons
  /** S42.C4: a list means "any of these" — the EAB page is reachable via the tenant-wide
   *  `manage_acme` or the narrow `manage_own_acme_eab`. */
  permission?: string | string[];
  requiresPlatformAdmin?: boolean;  // Sprint 31.9 Phase 11
}

interface NavSection {
  title: string;
  items: NavItem[];
}

// Simplified navigation matching mockup-1 primary nav
// Admin details moved to AdminLayout sub-sidebar
const navigation: NavSection[] = [
  {
    title: 'Dashboards',
    items: [
      { path: '/', label: 'Dashboard Explorer', icon: 'dashboard' },
    ],
  },
  {
    title: 'PKI Management',
    items: [
      { path: '/pki/certificates', label: 'Browse SSL/TLS Certs', icon: 'certificates' },
      { path: '/pki/acme', label: 'ACME Cert Orders', icon: 'orders' },
      { path: '/pki/eab', label: 'ACME EAB Pre-Auth', icon: 'orders', permission: ['manage_acme', 'manage_own_acme_eab'] },
      { path: '/pki/ssh', label: 'Browse SSH Certs', icon: 'ssh' },
      { path: '/pki/ssh-keys', label: 'SSH User Key Inventory', icon: 'ssh' },
      { path: '/pki/ssh-hosts', label: 'SSH Host Inventory', icon: 'ssh' },
    ],
  },
  {
    // Sprint 34.9: Tools section - utility services that don't require full authentication
    title: 'Tools',
    items: [
      { path: '/tools/validator', label: 'Certificate Validator', icon: 'validator' },
    ],
  },
  {
    title: '',  // No title for standalone links
    items: [
      { path: '/manage/overview', label: 'Administration', icon: 'admin', permission: 'manage_users' },
    ],
  },
];

export function Sidebar() {
  const { hasPermission, session } = useAuthStore();
  const sidebarCollapsed = useLayoutStore((state) => state.sidebarCollapsed);

  // Sprint 31.9 Phase 11: Check if current session is platform admin
  const isPlatformAdmin = session?.isPlatformAdmin ?? false;

  return (
    <nav className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`} aria-label="Main navigation">
      {/* Scrollable nav area */}
      <div className="sidebar-content">
        {/* Sprint 31.9 Phase 11: Tenant context header */}
        {session && !sidebarCollapsed && (
          <div 
            className="sidebar-tenant-context"
            style={{
              padding: '12px 16px',
              marginBottom: '8px',
              borderBottom: '1px solid var(--border-color)',
              background: 'var(--bg-secondary)',
            }}
          >
            <div style={{
              fontSize: '11px',
              fontWeight: 600,
              color: 'var(--text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '4px',
            }}>
              Current Tenant
            </div>
            <div style={{
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--text-primary)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {session.tenantName}
            </div>
            {isPlatformAdmin && (
              <div style={{
                marginTop: '4px',
                fontSize: '10px',
                fontWeight: 600,
                display: 'inline-block',
                padding: '2px 6px',
                borderRadius: '4px',
                background: 'var(--color-primary)',
                color: 'white',
              }}>
                PLATFORM ADMIN
              </div>
            )}
          </div>
        )}

        {navigation.map((section, index) => {
          // Filter items by permission and platform admin requirement
          const visibleItems = section.items.filter((item) => {
            // Check permission
            const required = item.permission
              ? (Array.isArray(item.permission) ? item.permission : [item.permission])
              : [];
            if (required.length > 0 && !required.some((p) => hasPermission(p))) {
              return false;
            }
            // Sprint 31.9 Phase 11: Check platform admin requirement
            if (item.requiresPlatformAdmin && !isPlatformAdmin) {
              return false;
            }
            return true;
          });
          
          // Skip sections with no visible items
          if (visibleItems.length === 0) return null;

          return (
            <div key={section.title || `section-${index}`} className="sidebar-section">
              {/* Section header - only show if title exists */}
              {section.title && (
                <div className="sidebar-section-title">
                  {section.title}
                </div>
              )}
              
              {/* Nav items with icons - Sprint 34.1 */}
              {visibleItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? 'active' : ''}`
                  }
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  {item.icon && (
                    <NavIcon icon={item.icon} size={18} style={{ marginRight: sidebarCollapsed ? '0' : '10px', opacity: 0.85 }} />
                  )}
                  {!sidebarCollapsed && <span>{item.label}</span>}
                </NavLink>
              ))}
            </div>
          );
        })}
      </div>

      {/* Footer section with VortexCA logo - Sprint 34.1 */}
      <div className="sidebar-footer">
        <div className="sidebar-footer-content">
          <VortexIcon width={28} style={{ marginRight: sidebarCollapsed ? '0' : '10px' }} />
          {!sidebarCollapsed && (
            <div>
              <div className="sidebar-brand">VORTEX CA</div>
              <div className="sidebar-tagline">Enterprise PKI</div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
